import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { downloadBlobFile } from '@shared/helpers';
import { Observable, of } from 'rxjs';
import { FileDataPropertyType, IFileDataPropertySchema, IFileDataSchema, IFileTransfer, IImportedData } from './interfaces';

export interface jsonImportError {
	error: string;
	path: string;
	value: string;
}

@Injectable({ providedIn: 'root' })
export class JsonFileTransfer<T> implements IFileTransfer<T, jsonImportError> {
	constructor(private translator: TranslateService) {}

	public exportData(fileName: string, data: T[], schema: IFileDataSchema<T>): Observable<T[]> {
		const fileData = data.map((row) => {
			const record: Record<string, any> = {};
			// potential performance issue here for very large datasets
			// as we are iterating over the schema for each row
			// could instead just rename the keys in the data
			for (const [key, propSchema] of Object.entries<IFileDataPropertySchema<any>>(schema.properties)) {
				const value = row[key];
				if ((value !== null && value !== undefined) || propSchema.required) {
					record[propSchema.fileKey || key] = this.buildFileValue(key, value, propSchema.type);
				}
			}
			return record;
		});

		downloadBlobFile(new Blob([JSON.stringify(fileData)], { type: 'application/json' }), `${fileName}.json`);
		return of(data); // Fallback in case of error
	}

	buildFileValue(path: string, value: any, propType: FileDataPropertyType<any>): any {
		if (typeof propType === 'string') {
			// eslint-disable-next-line valid-typeof
			if (typeof value !== propType) {
				throw new TypeError(
					this.translator.instant('transferService.json.errors.invalid-property-type', {
						path,
						expectedType: propType,
						actualType: typeof value,
					}),
				);
			}
			return value;
		}
		if (typeof propType === 'object') {
			if ('array' in propType) {
				const arrayType = propType.array;
				// Handle array type
				return Array.isArray(value) ? value.map((item, i) => this.buildFileValue(`${path}[${i}]`, item, arrayType)) : [];
			} else if ('record' in propType) {
				const recordType = propType.record;
				if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
					return Object.entries(value).reduce(
						(acc, [k, v]) => {
							acc[k] = this.buildFileValue(`${path}.${k}`, v, recordType);
							return acc;
						},
						{} as Record<string, any>,
					);
				} else {
					throw new TypeError(this.translator.instant('transferService.json.errors.invalid-property-type', { path, expectedType: 'record', actualType: typeof value }));
				}
			} else if ('schema' in propType) {
				if (typeof value !== 'object' || value === null || Array.isArray(value)) {
					throw new TypeError(
						this.translator.instant('transferService.json.errors.invalid-property-type', {
							path,
							expectedType: 'object',
							actualType: Array.isArray(value) ? 'array' : typeof value,
						}),
					);
				}

				const schema = propType.schema;
				const record = {} as T;
				for (const [key, propSchema] of Object.entries<IFileDataPropertySchema<any>>(schema.properties)) {
					const fileKey = propSchema.fileKey || key;
					const propValue = value[key];
					if (propValue !== undefined && propValue !== null) {
						record[fileKey as keyof T] = this.buildFileValue(`${path}.${fileKey}`, propValue, propSchema.type);
					} else if (propSchema.required) {
						throw new Error(this.translator.instant('transferService.json.errors.missing-required-field', { key: fileKey }));
					}
				}
				return record;
			}
			throw new Error('Invalid type definition');
		}
		throw new Error(
			this.translator.instant('transferService.json.errors.invalid-property-type', {
				path,
				expectedType: 'valid type',
				actualType: typeof value,
			}),
		);
	}

	importData(file: ArrayBuffer, schema: IFileDataSchema<T>, errorList: jsonImportError[]): Array<IImportedData<T>> {
		if (file.byteLength === 0) {
			throw new Error(this.translator.instant('transferService.json.errors.empty-file'));
		}

		// Read the file as text
		const fileText = new TextDecoder('utf-8').decode(file);

		let jsonData: any;
		try {
			jsonData = JSON.parse(fileText);
		} catch {
			throw new Error(this.translator.instant('transferService.json.errors.invalid-json'));
		}

		// remove header
		if (!Array.isArray(jsonData) || jsonData.length === 0) {
			throw new Error(this.translator.instant('transferService.json.errors.missing-data'));
		}

		try {
			const result: Array<IImportedData<T>> = jsonData.reduce((acc: Array<IImportedData<T>>, row: any, rowIndex: number) => {
				const itemErrorList: jsonImportError[] = [];
				const value = this.buildImportValue(`[row ${rowIndex + 1}]`, row, { schema }, itemErrorList);

				if (itemErrorList.length === 0) {
					acc.push({
						data: value,
						location: this.translator.instant('transferService.json.row-location', {
							index: rowIndex + 1,
						}),
					});
				} else errorList.push(...itemErrorList);

				return acc;
			}, []);

			return result;
		} catch (e: any) {
			throw new Error(
				this.translator.instant('transferService.json.errors.invalid-json', {
					error: e.message,
				}),
			);
		}
	}

	buildImportValue<U>(path: string, value: any, propType: FileDataPropertyType<U>, errorList: jsonImportError[]): any {
		if (value === null || value === undefined) {
			return value;
		}

		if (typeof propType === 'string') {
			// eslint-disable-next-line valid-typeof
			if (typeof value !== propType) {
				const msg = this.translator.instant('transferService.json.errors.invalid-property-type', { expectedType: propType, actualType: typeof value });
				errorList.push({ error: msg, path, value: String(value) });
			}
			return value;
		}
		if (typeof propType === 'object') {
			if ('array' in propType) {
				const arrayType = propType.array;

				if (!Array.isArray(value)) {
					const msg = this.translator.instant('transferService.json.errors.invalid-property-type', { expectedType: 'array', actualType: typeof value });
					errorList.push({ error: msg, path, value: String(value) });
				}

				// Handle array type
				return Array.isArray(value) ? value.map((item, index) => this.buildImportValue(`${path}[${index}]`, item, arrayType, errorList)) : [];
			} else if ('record' in propType) {
				const recordType = propType.record;
				if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
					return Object.entries(value).reduce(
						(acc, [k, v]) => {
							acc[k] = this.buildImportValue(`${path}.${k}`, v, recordType, errorList);
							return acc;
						},
						{} as Record<string, any>,
					);
				} else {
					const msg = this.translator.instant('transferService.json.errors.invalid-property-type', { expectedType: 'record', actualType: typeof value });
					errorList.push({ error: msg, path, value: String(value) });
				}
			} else if ('schema' in propType) {
				if (typeof value !== 'object' || value === null || Array.isArray(value)) {
					const msg = this.translator.instant('transferService.json.errors.invalid-property-type', {
						expectedType: Array.isArray(value) ? 'array' : 'object',
						actualType: typeof value,
					});
					errorList.push({ error: msg, path, value: String(value) });
					return {} as T;
				}

				const schema = propType.schema;
				const record = {} as T;
				const propertyKeys: [string, { key: string; propSchema: IFileDataPropertySchema<any> }][] = Object.entries<IFileDataPropertySchema<any>>(schema.properties).map(([key, propSchema]) => [propSchema.fileKey || key, { key, propSchema }]);
				for (const [fileKey, keySchema] of propertyKeys) {
					const propValue = value[fileKey];
					const propPath = `${path}.${fileKey}`;
					if (propValue !== undefined && propValue !== null) {
						record[keySchema.key as keyof T] = this.buildImportValue(propPath, propValue, keySchema.propSchema.type, errorList);
					} else if (keySchema.propSchema.required) {
						const msg = this.translator.instant('transferService.json.errors.missing-required-field', { key: fileKey });
						errorList.push({ error: msg, path: propPath, value: String(propValue) });
					}
				}
				return record;
			}
			throw new Error('Invalid type definition');
		}
		throw new Error(
			this.translator.instant('transferService.json.errors.invalid-property-type', {
				expectedType: 'valid type',
				actualType: typeof value,
			}),
		);
	}
}
